import { useState } from 'react';
import { Search as SearchIcon, Loader2 } from 'lucide-react';
import { useConversations } from '@/hooks/use-conversations';
import { ConversationCard } from '@/components/conversations';

export function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Simple debounce using setTimeout
    const timer = setTimeout(() => {
      setDebouncedQuery(value);
    }, 300);

    return () => clearTimeout(timer);
  };

  const { data, isLoading } = useConversations(
    debouncedQuery ? { search: debouncedQuery } : {}
  );

  const conversations = data?.pages.flatMap((page) => page.conversations) ?? [];
  const hasQuery = debouncedQuery.length > 0;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Search input */}
      <div className="relative mb-6">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search conversations, transcripts, speakers..."
          className="w-full pl-12 pr-4 py-3 text-lg rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          autoFocus
        />
      </div>

      {/* Results */}
      {!hasQuery ? (
        <div className="text-center py-12">
          <SearchIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            Start typing to search your conversations
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Search by title, speaker names, or transcript content
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            No results found for "{debouncedQuery}"
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Try different keywords or check your spelling
          </p>
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-500 mb-4">
            {conversations.length} result{conversations.length !== 1 ? 's' : ''} for "{debouncedQuery}"
          </p>
          <div className="space-y-3">
            {conversations.map((conversation) => (
              <ConversationCard key={conversation.id} conversation={conversation} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
